export type LeadStatus = 'new' | 'contacted' | 'meeting' | 'proposal' | 'negotiating' | 'won' | 'lost' | 'onhold';
export type LeadPriority = 'low' | 'medium' | 'high' | 'urgent';
export type ActivityType = 'call' | 'email' | 'meeting' | 'note' | 'linkedin' | 'demo' | 'proposal';
export type EnrichmentStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface Lead {
  id: string;
  name: string;
  website?: string;
  industry?: string;
  size_range?: string;
  job_title?: string;
  job_description?: string;
  job_posting_url?: string;
  priority: LeadPriority;
  status: LeadStatus;
  notes?: string;
  source?: string;
  lead_score: number;
  revenue_range?: string;
  next_contact_at?: string;
  last_contact?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  // New fields for news intelligence
  geographic_area?: string;
  news_monitoring_enabled: boolean;
  // New enrichment fields
  enrichment_status?: string;
  last_enrichment_run_id?: string;
  // New conversion tracking fields
  converted_to_client_id?: string;
  conversion_date?: string;
  // Corporate phone field
  corporate_phone?: string;
  // Related data
  contacts?: LeadContact[];
  tags?: LeadTag[];
  activities?: LeadActivity[];
  attachments?: LeadAttachment[];
  latest_enrichment_result?: LeadEnrichmentResult | null;
  news_alerts?: NewsAlert[];
}

export interface LeadEnrichmentResult {
  id: string;
  lead_id: string;
  run_id: string;
  status: EnrichmentStatus;
  progress_stage?: string;
  priority?: string;
  workflow_input?: any;
  enriched_data?: any;
  error_message?: string;
  lutra_results_url?: string;
  estimated_completion_at?: string;
  processing_started_at?: string;
  next_poll_at?: string;
  poll_attempts?: number;
  requested_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
}

export interface NewsAlert {
  id: string;
  lead_id: string;
  title: string;
  content?: string;
  url?: string;
  source?: string;
  published_at?: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export interface LeadContact {
  id: string;
  lead_id: string;
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  linkedin_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface LeadTag {
  id: string;
  lead_id: string;
  tag: string;
  created_by?: string;
  created_at: string;
}

export interface LeadActivity {
  id: string;
  lead_id: string;
  activity_type: ActivityType;
  title: string;
  description?: string;
  performed_at: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface LeadAttachment {
  id: string;
  lead_id: string;
  file_name: string;
  file_url: string;
  file_type?: string;
  description?: string;
  created_by: string;
  created_at: string;
}

export interface CompanyEnrichmentResult {
  id: string;
  lead_id: string;
  status: EnrichmentStatus;
  result_data?: any;
  workflow_input?: any;
  error_message?: string;
  run_id?: string;
  location_url?: string;
  requested_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export const leadStatusLabels: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  meeting: 'Meeting Scheduled',
  proposal: 'Proposal Sent',
  negotiating: 'Negotiating',
  won: 'Won',
  lost: 'Lost',
  onhold: 'On Hold'
};

export const leadPriorityLabels: Record<LeadPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent'
};

export const activityTypeLabels: Record<ActivityType, string> = {
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  note: 'Note',
  linkedin: 'LinkedIn',
  demo: 'Demo',
  proposal: 'Proposal'
};

// Pipeline stages configuration
export const LEAD_PIPELINE_STAGES = [
  { id: 'new' as LeadStatus, label: 'New', color: 'bg-blue-100 text-blue-800' },
  { id: 'contacted' as LeadStatus, label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'meeting' as LeadStatus, label: 'Meeting', color: 'bg-purple-100 text-purple-800' },
  { id: 'proposal' as LeadStatus, label: 'Proposal', color: 'bg-orange-100 text-orange-800' },
  { id: 'negotiating' as LeadStatus, label: 'Negotiating', color: 'bg-amber-100 text-amber-800' },
  { id: 'won' as LeadStatus, label: 'Won', color: 'bg-green-100 text-green-800' },
  { id: 'lost' as LeadStatus, label: 'Lost', color: 'bg-red-100 text-red-800' },
  { id: 'onhold' as LeadStatus, label: 'On Hold', color: 'bg-gray-100 text-gray-800' }
];
